import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGetVendorMeQuery } from '../../store/vendorApi';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

const ONBOARDING_DEFERRED_KEY = 'onboardingDeferred';

/**
 * When an authenticated vendor hasn't completed onboarding, redirects to /onboarding
 * from any protected route. If the user clicked "Complete later", they can freely
 * access the app until the session ends (sessionStorage flag). Settings is always allowed.
 */
const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const location = useLocation();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { data: vendor, isLoading: isLoadingVendor } = useGetVendorMeQuery(undefined, {
    skip: user?.role !== 'vendor',
  });

  const pathname = location.pathname;
  const isSettings = pathname.startsWith('/settings');
  const hasDeferredOnboarding = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(ONBOARDING_DEFERRED_KEY) === 'true';

  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (user?.role !== 'vendor') {
    return <>{children}</>;
  }

  if (isLoadingVendor) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (vendor && !vendor.onboardingCompleted && !isSettings && !hasDeferredOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default OnboardingGuard;
